import { type SecurityEvent } from '@/mocks/saSecurity';
import { EVENT_TYPE_LABELS, RISK_LEVEL_LABELS } from './securityLabels';

interface Props {
  event: SecurityEvent | null;
  onClose: () => void;
  onAction: (action: string, event: SecurityEvent) => void;
}

export default function SecurityEventDetailModal({ event, onClose, onAction }: Props) {
  if (!event) return null;

  const typeMeta = EVENT_TYPE_LABELS[event.type] || { label: event.type, icon: 'ri-question-line', color: 'bg-background-100 text-foreground-500' };
  const riskMeta = RISK_LEVEL_LABELS[event.riskLevel] || { label: event.riskLevel, color: 'bg-background-100 text-foreground-500' };

  return (
    <>
      <div className="fixed inset-0 z-[80] bg-black/40" onClick={onClose}></div>
      <div className="fixed inset-y-0 right-0 z-[90] w-full max-w-lg bg-background-50 border-l border-background-200 shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${typeMeta.color}`}>
              <i className={`${typeMeta.icon} text-base`}></i>
            </div>
            <div className="min-w-0">
              <p className="text-foreground-950 text-sm font-bold truncate">{typeMeta.label}</p>
              <p className="text-foreground-400 text-[10px]">{event.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Risk + Resolved */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold border ${riskMeta.color}`}>
              Riesgo: {riskMeta.label}
            </span>
            {event.resolved ? (
              <span className="inline-flex items-center gap-1 text-emerald-600 text-[10px] font-medium">
                <i className="ri-check-line text-[9px]"></i>
                Resuelto {event.resolvedBy !== '—' ? `por ${event.resolvedBy}` : ''}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-600 text-[10px] font-medium">
                <i className="ri-time-line text-[9px]"></i>
                Pendiente de resolución
              </span>
            )}
          </div>

          {/* Details */}
          <div className="space-y-3">
            <p className="text-foreground-800 text-xs">{event.details}</p>
          </div>

          {/* Info grid */}
          <div className="grid grid-cols-2 gap-3">
            <InfoBlock label="Usuario" value={event.userName} sub={event.userRole} />
            <InfoBlock label="Radio" value={event.radioName} sub={event.radioId} />
            <InfoBlock label="IP" value={event.ipAddress} sub={`${event.city}, ${event.country}`} mono />
            <InfoBlock label="Dispositivo" value={event.device} sub={`${event.browser} · ${event.os}`} />
          </div>

          {/* Timestamp */}
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[10px] mb-1">Fecha y hora</p>
            <p className="text-foreground-800 text-xs font-medium">{event.timestamp}</p>
          </div>

          {/* Audit Log link */}
          {event.auditLogId && (
            <div className="bg-accent-50 border border-accent-200 rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                  <i className="ri-links-line text-accent-500 text-sm"></i>
                </div>
                <div>
                  <p className="text-accent-800 text-[10px] font-semibold">Audit Log vinculado</p>
                  <p className="text-accent-600 text-[10px]">{event.auditLogId}</p>
                </div>
              </div>
              <button
                onClick={() => onAction('link_audit', event)}
                className="px-3 py-1.5 rounded-lg bg-white text-accent-700 text-[10px] font-medium cursor-pointer hover:bg-accent-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-external-link-line text-[9px] mr-1"></i>
                Ver en Audit Log
              </button>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 border-t border-background-200 shrink-0 space-y-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onAction('investigate', event)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-search-eye-line text-sm"></i>
              Investigar
            </button>
            {!event.resolved && (
              <button
                onClick={() => onAction('mark_safe', event)}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-xs font-bold cursor-pointer hover:bg-emerald-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-shield-check-line text-sm"></i>
                Marcar seguro
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onAction('block_ip', event)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-red-50 text-red-600 text-[11px] font-medium cursor-pointer hover:bg-red-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-forbid-line text-xs"></i>
              Bloquear IP
            </button>
            <button
              onClick={() => onAction('block_user', event)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-red-50 text-red-600 text-[11px] font-medium cursor-pointer hover:bg-red-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-user-unfollow-line text-xs"></i>
              Bloquear usuario
            </button>
            <button
              onClick={() => onAction('close_session', event)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-red-50 text-red-600 text-[11px] font-medium cursor-pointer hover:bg-red-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-logout-box-r-line text-xs"></i>
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function InfoBlock({ label, value, sub, mono }: { label: string; value: string; sub: string; mono?: boolean }) {
  return (
    <div className="bg-background-100 rounded-xl p-3">
      <p className="text-foreground-400 text-[10px] mb-1">{label}</p>
      <p className={`text-foreground-800 text-xs font-medium ${mono ? 'font-mono' : ''}`}>{value}</p>
      <p className="text-foreground-400 text-[10px]">{sub}</p>
    </div>
  );
}